// Desktop Chrome/Edge ignore the manifest's `display: fullscreen`, so the player asks
// for fullscreen itself: straight away where the AutomaticFullscreenAllowedForUrls policy
// grants it, otherwise on the first click / key press — the API needs a user gesture.

import { installState } from './webInstall';

export type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
};
export type FsElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
export const fsDoc = document as FsDocument;
export const fsRoot = document.documentElement as FsElement;

const STORAGE_KEY = 'sp:web-fullscreen-on-start';

// iPhone Safari only grew the Fullscreen API in iOS 17.2; iPad and desktop
// Safari < 16.4 have the webkit-prefixed variant.
export function fullscreenSupported(): boolean {
  const enabled = document.fullscreenEnabled ?? fsDoc.webkitFullscreenEnabled ?? true;
  return enabled && !!(fsRoot.requestFullscreen || fsRoot.webkitRequestFullscreen);
}

export function isFullscreen(): boolean {
  return !!(document.fullscreenElement || fsDoc.webkitFullscreenElement);
}

export async function toggleFullscreen(): Promise<void> {
  try {
    if (isFullscreen()) {
      await (document.exitFullscreen ? document.exitFullscreen() : fsDoc.webkitExitFullscreen?.());
    } else {
      await (fsRoot.requestFullscreen ? fsRoot.requestFullscreen({ navigationUI: 'hide' }) : fsRoot.webkitRequestFullscreen?.());
    }
  } catch (err) {
    console.warn('[WebFullscreen] fullscreen toggle failed', err);
  }
}

/** Must be called synchronously from a gesture handler unless the policy grants it. */
export async function enterFullscreen(): Promise<void> {
  try {
    await (fsRoot.requestFullscreen ? fsRoot.requestFullscreen({ navigationUI: 'hide' }) : fsRoot.webkitRequestFullscreen?.());
  } catch (err) {
    console.warn('[WebFullscreen] enter fullscreen failed', err);
  }
}

export function fullscreenOnStart(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === '1' || stored === '0') return stored === '1';
  } catch {
    /* storage blocked — fall back to the default */
  }
  return installState() === 'standalone';
}

export function setFullscreenOnStart(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* storage blocked — the setting lasts for this page only */
  }
  if (!on) disarm();
}

let autoAllowed: Promise<boolean> | null = null;

/** Chrome 128+ reports the Automatic Fullscreen content setting; elsewhere the query throws. */
export function autoFullscreenAllowed(): Promise<boolean> {
  // navigator.permissions itself is absent on old WebKit / insecure contexts — the whole body must be guarded.
  autoAllowed ??= (async () => {
    try {
      const status = await navigator.permissions.query({ name: 'fullscreen', allowWithoutGesture: true } as unknown as PermissionDescriptor);
      return status.state === 'granted';
    } catch {
      return false;
    }
  })();
  return autoAllowed;
}

let pending = false;
const pendingListeners = new Set<(pending: boolean) => void>();

export function onFullscreenPending(fn: (pending: boolean) => void): void {
  pendingListeners.add(fn);
  if (pending) fn(true);
}

function notify(): void {
  for (const fn of pendingListeners) fn(pending);
}

const IGNORED_KEYS = new Set(['Escape', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock']);

function onGesture(e: Event): void {
  if (e.type === 'keydown' && IGNORED_KEYS.has((e as KeyboardEvent).key)) return;
  // The settings modal, the issues panel and text fields have their own handling.
  const target = e.target;
  if (target instanceof Element && (target.closest('.web-settings, .web-issues-panel') || target.matches('input, textarea, [contenteditable]'))) {
    return;
  }
  // Only the fullscreenchange listener disarms: a refused request must keep waiting.
  void enterFullscreen();
}

function arm(): void {
  if (pending) return;
  pending = true;
  window.addEventListener('pointerdown', onGesture, true);
  window.addEventListener('keydown', onGesture, true);
  notify();
}

function disarm(): void {
  if (!pending) return;
  window.removeEventListener('pointerdown', onGesture, true);
  window.removeEventListener('keydown', onGesture, true);
  pending = false;
  notify();
}

function onFullscreenChange(): void {
  if (isFullscreen()) disarm();
}

export function installWebFullscreen(): void {
  // display-mode: fullscreen = an Android installed PWA or a kiosk window — already there.
  if (!fullscreenSupported() || isFullscreen() || window.matchMedia?.('(display-mode: fullscreen)')?.matches || !fullscreenOnStart()) return;
  document.addEventListener('fullscreenchange', onFullscreenChange);
  document.addEventListener('webkitfullscreenchange', onFullscreenChange);
  void autoFullscreenAllowed()
    .then(async allowed => {
      if (allowed) await enterFullscreen();
    })
    .finally(() => {
      if (!isFullscreen()) arm();
    });
}
